import {Currency} from '@/utils/currency'
import {findCatalogProduct, getUnitPrice} from '@/utils/pricing/catalog'
import {createFeeItem} from '@/utils/pricing/helpers'
import {Fee} from '@/utils/pricing/types'

type ProductFeeInput = {
  currency: Currency
  numberOfClasses?: number
  quantity?: number
  vatRate: number
}

// Builds a Fee entirely from a catalog product: unit price (per-class rules),
// invoice label, gov-fee flag and product id all come from the product row. The
// caller only names the product (category, name) and supplies user input.
export const buildProductFee = (category: string, name: string, input: ProductFeeInput): Fee => {
  const product = findCatalogProduct(category, name)
  if (!product) {
    throw new Error(`No catalog product found for sku "${category}.${name}"`)
  }
  // A product with no invoice line text is non-invoiceable and unorderable, so
  // reaching it here means a flow is quoting something it shouldn't.
  if (!product.invoice_translation_key) {
    throw new Error(`Catalog product "${category}.${name}" has no invoice_translation_key`)
  }

  return createFeeItem(
    product.invoice_translation_key,
    getUnitPrice(product, input.numberOfClasses ?? 1, input.currency),
    input.quantity ?? 1,
    product.is_gov_fee ? 0 : input.vatRate,
    product.is_gov_fee,
    product.id,
  )
}

// Like buildProductFee but returns null instead of throwing when the SKU isn't in
// the catalog — for flows where a service or gov side may legitimately be absent.
export const buildProductFeeIfExists = (
  category: string,
  name: string,
  input: ProductFeeInput,
): Fee | null =>
  findCatalogProduct(category, name) ? buildProductFee(category, name, input) : null
